import type { ApplicationHost, GameData } from '../app/GameApplication';
import {
  FRESH_TIRE_HEALTH,
  FixedTickAccumulator,
  hashPhysicsConfig,
  STEP_MS,
  stateHash,
  validateReplayEnvelope,
  type ReplayEnvelopeV1,
} from '@cars/shared';
import { VehicleRuntime } from './VehicleRuntime';
import { CAR_CONFIGS } from '../data/cars';
import { TRACK_CONFIGS } from '../data/tracks';
import { LapTimer } from '../../utils/LapTimer';
import { HudOverlay } from '../../ui/HudOverlay';
import { CameraZoomController } from '../camera/CameraZoomController';
import { ReplayInputSource } from '../replay/ReplayInputSource';
import { readReducedCameraMotion } from '../camera/reducedCameraMotion';
import { ViewportDebugOverlay } from '../camera/ViewportDebugOverlay';

export class ReplaySession {
  private readonly host: ApplicationHost;
  constructor(host: ApplicationHost) {
    this.host = host;
  }
  private replayData: ReplayEnvelopeV1 | null = null;
  private replaySource: ReplayInputSource | null = null;
  private vehicle: VehicleRuntime | null = null;
  private get replay(): ReplayEnvelopeV1 {
    if (!this.replayData) throw new Error('Replay is not loaded');
    return this.replayData;
  }
  private get source(): ReplayInputSource {
    if (!this.replaySource) throw new Error('Replay input is not loaded');
    return this.replaySource;
  }
  private get car(): VehicleRuntime {
    if (!this.vehicle) throw new Error('Replay vehicle is not loaded');
    return this.vehicle;
  }
  private readonly accumulator = new FixedTickAccumulator();
  private readonly cameraZoomController = new CameraZoomController();
  private hudOverlay: HudOverlay | null = null;
  private viewportDebugOverlay: ViewportDebugOverlay | null = null;
  private controlsOverlay: HTMLDivElement | null = null;
  private simulationTick = 0;
  private playbackSpeed = 1;
  private isPaused = false;
  private isReady = false;
  private trackBounds = { width: 0, height: 0 };
  private username = '';
  private replayEnded = false;
  private playbackFailed = false;
  private pauseButton: HTMLButtonElement | null = null;
  private expectedStateHashes: ReadonlyMap<number, string> = new Map();
  private expectedCheckpoints: ReadonlyMap<number, number> = new Map();
  async create(data: GameData): Promise<void> {
    if (!data.replay) throw new Error('Replay data is required');
    this.cameraZoomController.reset();
    this.playbackSpeed = 1;
    validateReplayEnvelope(data.replay);
    const replay = data.replay;
    this.username = data.username?.trim() ?? '';
    const trackConfig = TRACK_CONFIGS[replay.trackId];
    const carConfig = CAR_CONFIGS[replay.carId];
    if (!trackConfig || !carConfig)
      throw new Error('Replay references an unavailable car or track');
    if (hashPhysicsConfig(carConfig) !== replay.carConfigHash)
      throw new Error('Replay car config hash mismatch');

    this.replayData = replay;
    this.replaySource = new ReplayInputSource(replay);
    this.expectedStateHashes = new Map(replay.stateHashes.map((entry) => [entry.tick, entry.hash]));
    this.expectedCheckpoints = new Map(
      replay.checkpoints.map((checkpoint) => [checkpoint.tick, checkpoint.index]),
    );
    this.simulationTick = replay.startTick - 1;
    this.accumulator.reset();
    this.isPaused = false;
    this.replayEnded = false;

    const assets = await this.host.assets.loadRace(trackConfig, carConfig, this.host.signal, {
      maxTextureSize: this.host.renderer.maxTextureSize,
    });
    if (!this.host.isActive()) {
      assets.scene?.model.dispose();
      return;
    }
    this.trackBounds = {
      width: assets.landscape.naturalWidth,
      height: assets.landscape.naturalHeight,
    };
    this.host.renderer.load(assets);
    if (assets.track.getMapHash() !== replay.trackMapHash)
      throw new Error('Replay track map hash mismatch');

    this.vehicle = new VehicleRuntime(
      trackConfig.startPointX,
      trackConfig.startPointY,
      trackConfig.startPointRotation,
      assets.track,
      carConfig,
    );
    this.car.setTimer(new LapTimer());
    this.car.restoreState(replay.initialState);
    this.car.resetCheckpointState();
    this.cameraZoomController.setReducedMotion(readReducedCameraMotion());
    this.resize();
    this.host.uiRoot.replaceChildren();
    this.host.renderer.present(this.car.getInterpolatedPose(0));
    const uiLayer = this.host.uiRoot;
    if (!uiLayer) throw new Error('Missing #ui-layer element');
    this.hudOverlay = new HudOverlay(uiLayer, trackConfig, this.trackBounds.width);
    this.controlsOverlay = this.createControlsOverlay(uiLayer);
    if (import.meta.env.VITE_VIEWPORT_DEBUG === 'true')
      this.viewportDebugOverlay = new ViewportDebugOverlay(uiLayer, () =>
        this.host.renderer.renderer.forceContextLoss(),
      );
    this.isReady = true;
    this.presentCurrent(0);
    // Asset decode, GPU upload and the first render are loading time.
    this.host.resetFrameClock();
  }

  update(delta: number): void {
    if (!this.isReady || this.isPaused || this.playbackFailed) return;
    this.host.renderer.recordFrame(delta);
    const result = this.accumulator.advance(delta * this.playbackSpeed, () => this.stepTick());
    if (result.invalidated) {
      this.stopForDrift('Playback stalled. Restart the replay to continue.');
      return;
    }
    const telemetry = this.car.getTelemetry();
    this.cameraZoomController.update(
      delta * this.playbackSpeed,
      telemetry.speedRatio,
      telemetry.wallHit,
    );
    this.host.renderer.setZoom(this.cameraZoomController.getZoom());
    this.presentCurrent(result.alpha);
  }

  private presentCurrent(alpha: number): void {
    const pose = this.car.getInterpolatedPose(alpha);
    const telemetry = this.car.getTelemetry();
    this.host.renderer.present(pose, true, this.car.getPathTrace());
    this.viewportDebugOverlay?.update(this.host.renderer.getMetrics());
    this.hudOverlay?.setCurrentTime(
      Math.max(0, this.simulationTick - this.replay.startTick + 1) * STEP_MS,
    );
    this.hudOverlay?.setSpeed(telemetry.speedRatio);
    this.hudOverlay?.setTireHealth(this.car.getState().tires ?? FRESH_TIRE_HEALTH);
    this.hudOverlay?.setZoneStatus(
      telemetry.pitSpeedLimitActive ?? false,
      telemetry.pitServiceActive ?? false,
      telemetry.pitServiceVisit ?? false,
    );
    this.hudOverlay?.setMinimapCarPoint(pose.x, pose.y);
    this.updateProgressControl();
  }

  private stepTick(): void {
    if (this.isPaused || this.replayEnded || this.playbackFailed) return;
    if (this.simulationTick >= this.replay.endTick) {
      this.finishPlayback();
      return;
    }
    this.simulationTick += 1;
    const result = this.car.stepReplayOnce(
      STEP_MS,
      this.source.controlsAt(this.simulationTick),
      this.simulationTick,
    );
    const expectedHash = this.expectedStateHashes.get(this.simulationTick);
    if (expectedHash && stateHash(this.car.getState()) !== expectedHash) {
      this.stopForDrift(`Replay state drift at tick ${this.simulationTick}`);
      return;
    }
    const expectedCheckpoint = this.expectedCheckpoints.get(this.simulationTick);
    if ((result.checkpointIndex !== undefined) !== (expectedCheckpoint !== undefined)) {
      this.stopForDrift(`Replay checkpoint drift at tick ${this.simulationTick}`);
      return;
    }
    if (expectedCheckpoint !== undefined && result.checkpointIndex !== expectedCheckpoint) {
      this.stopForDrift(`Replay checkpoint drift at tick ${this.simulationTick}`);
    }
  }

  private seek(targetTick: number): void {
    const target = Math.max(
      this.replay.startTick - 1,
      Math.min(this.replay.endTick, Math.round(targetTick)),
    );
    this.accumulator.reset();
    const wasPaused = this.isPaused;
    this.isPaused = false;
    // Replaying from the canonical initial state must consume events from the
    // beginning. Seeking the input cursor to the destination would apply the
    // destination's controls to every earlier tick and change physics state.
    this.source.reset();
    this.car.restoreState(this.replay.initialState);
    this.car.resetCheckpointState();
    this.simulationTick = this.replay.startTick - 1;
    this.replayEnded = false;
    this.playbackFailed = false;
    if (this.pauseButton) this.pauseButton.disabled = false;
    if (this.pauseButton) this.pauseButton.textContent = 'Pause';
    const error = this.controlsOverlay?.querySelector<HTMLElement>('.replay-error');
    if (error) error.textContent = '';
    while (this.simulationTick < target && !this.isPaused) this.stepTick();
    // Synchronous canonical seeking is loading work, not elapsed playback time.
    this.host.resetFrameClock();
    if (!this.replayEnded && !this.playbackFailed) {
      this.isPaused = wasPaused;
      if (this.pauseButton) this.pauseButton.textContent = wasPaused ? 'Resume' : 'Pause';
    }
    this.presentCurrent(1);
  }

  private createControlsOverlay(uiLayer: HTMLElement): HTMLDivElement {
    const root = document.createElement('div');
    root.className = 'replay-controls';
    const label = document.createElement('strong');
    label.textContent = 'REPLAY';
    label.setAttribute('aria-label', 'Replay playback');
    const pause = document.createElement('button');
    pause.type = 'button';
    pause.className = 'ui-button';
    pause.textContent = 'Pause';
    this.pauseButton = pause;
    pause.addEventListener('click', () => {
      if (this.replayEnded) {
        this.seek(this.replay.startTick - 1);
        this.isPaused = false;
        return;
      }
      this.isPaused = !this.isPaused;
      if (!this.isPaused) this.host.resetFrameClock();
      pause.textContent = this.isPaused ? 'Resume' : 'Pause';
    });
    const restart = document.createElement('button');
    restart.type = 'button';
    restart.className = 'ui-button ui-button--subtle';
    restart.textContent = 'Restart';
    restart.addEventListener('click', () => {
      this.seek(this.replay.startTick - 1);
      this.isPaused = false;
      pause.textContent = 'Pause';
    });
    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'ui-button ui-button--subtle';
    back.textContent = 'Leaderboard';
    back.addEventListener('click', () => {
      this.host.showMenu({
        username: this.username,
        showScoreboard: true,
      });
    });
    const seek = document.createElement('input');
    seek.type = 'range';
    seek.min = String(this.replay.startTick - 1);
    seek.max = String(this.replay.endTick);
    seek.value = String(this.replay.startTick - 1);
    seek.step = '1';
    seek.setAttribute('aria-label', 'Seek replay');
    seek.addEventListener('input', () => this.seek(Number(seek.value)));
    const speed = document.createElement('select');
    speed.setAttribute('aria-label', 'Playback speed');
    for (const value of [0.5, 1, 2]) {
      const option = document.createElement('option');
      option.value = String(value);
      option.textContent = `${value}x`;
      if (value === 1) option.selected = true;
      speed.appendChild(option);
    }
    speed.addEventListener('change', () => {
      this.playbackSpeed = Number(speed.value);
    });
    root.append(label, pause, restart, seek, speed, back);
    const error = document.createElement('div');
    error.className = 'replay-error';
    error.setAttribute('role', 'alert');
    root.appendChild(error);
    uiLayer.appendChild(root);
    return root;
  }

  private updateProgressControl(): void {
    const seek = this.controlsOverlay?.querySelector<HTMLInputElement>('input[type="range"]');
    if (seek && document.activeElement !== seek) seek.value = String(this.simulationTick);
  }

  private finishPlayback(): void {
    this.isPaused = true;
    this.replayEnded = true;
    if (this.pauseButton) this.pauseButton.textContent = 'Replay Again';
  }

  private stopForDrift(message: string): void {
    this.playbackFailed = true;
    this.isPaused = true;
    if (this.pauseButton) this.pauseButton.disabled = true;
    this.replayEnded = true;
    if (this.pauseButton) this.pauseButton.textContent = 'Replay Again';
    const error = this.controlsOverlay?.querySelector<HTMLElement>('.replay-error');
    if (error) error.textContent = message;
  }
  resize(): void {
    this.cameraZoomController.setViewport(this.host.renderer.getSize(), this.trackBounds);
    this.host.renderer.setZoom(this.cameraZoomController.getZoom());
    this.host.renderer.redraw();
    this.viewportDebugOverlay?.update(this.host.renderer.getMetrics());
  }

  dispose(): void {
    this.isReady = false;
    this.hudOverlay?.destroy();
    this.hudOverlay = null;
    this.controlsOverlay?.remove();
    this.controlsOverlay = null;
    this.pauseButton = null;
    this.viewportDebugOverlay?.destroy();
    this.viewportDebugOverlay = null;
  }
}
